import { Button, Stack, Typography, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useContext, useEffect, useReducer, useState, useMemo } from 'react';
import { IExpert, IPrivateService } from '../../types';

import { ArrowBack, Close } from '@mui/icons-material';
import constants from '../../config/constants';
import { AlertContext } from '../../context/alert.context';
import { useLanguage } from '../../context/language.context';
import { useModal } from '../../context/ModalProvider';
import useFetch from '../../hooks/useFetch';
import TimeInput from '../TimeInput';
import VacationDialog from './VacationDialog';
import { workingHoursReducer, WorkingHoursState } from './WorkingHoursReducer';

export type ServiceSelection = {
    id: string;
    service_available_days: Array<{ day: number; start_time: string; end_time: string }>;
};
export type TimeRange = { from: number; to: number; _id: string; services?: ServiceSelection[] | 'all' };
const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
export type WeeklySchedule = {
    monday: TimeRange[];
    tuesday: TimeRange[];
    wednesday: TimeRange[];
    thursday: TimeRange[];
    friday: TimeRange[];
    saturday: TimeRange[];
    sunday: TimeRange[];
};
const initialState: WorkingHoursState = {
    schedule: { sunday: [], monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [] },
    updated: false,
};

const WorkingHoursServices = ({ profileData }: { profileData?: IExpert }) => {
    const [state, dispatch] = useReducer(workingHoursReducer, initialState);
    const scheduleData = useFetch<WeeklySchedule>(`${constants.API_URL}/instructor/admin/schedule`);
    const alert = useContext(AlertContext);
    const { t } = useLanguage();
    const privateServicesData = useFetch<IPrivateService[]>(`${constants.API_URL}/instructor/admin/private_services`);
    const dayNames = [t('sunday'), t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday')];

    // Helper function to convert "HH:mm" to number format (e.g., "09:00" -> 900)
    const convertTimeToNumber = (timeStr: string): number => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 100 + minutes;
    };

    useEffect(() => {
        if (scheduleData.data && privateServicesData.data) {
            // Start with the existing schedule, ensuring each day is an array
            const mergedSchedule: WeeklySchedule = {
                sunday: Array.isArray(scheduleData.data.sunday) ? [...scheduleData.data.sunday] : [],
                monday: Array.isArray(scheduleData.data.monday) ? [...scheduleData.data.monday] : [],
                tuesday: Array.isArray(scheduleData.data.tuesday) ? [...scheduleData.data.tuesday] : [],
                wednesday: Array.isArray(scheduleData.data.wednesday) ? [...scheduleData.data.wednesday] : [],
                thursday: Array.isArray(scheduleData.data.thursday) ? [...scheduleData.data.thursday] : [],
                friday: Array.isArray(scheduleData.data.friday) ? [...scheduleData.data.friday] : [],
                saturday: Array.isArray(scheduleData.data.saturday) ? [...scheduleData.data.saturday] : [],
            };
            
            // Process each service's service_available_days
            privateServicesData.data.forEach((service) => {
                if (service.active !== false && service.service_available_days && service.service_available_days.length > 0) {
                    service.service_available_days.forEach((daySlot) => {
                        const dayName = days[daySlot.day] as keyof WeeklySchedule;
                        if (dayName) {
                            const from = convertTimeToNumber(daySlot.start_time);
                            const to = convertTimeToNumber(daySlot.end_time);
                            
                            // Check if this time slot already exists
                            const existingSlots = mergedSchedule[dayName] || [];
                            const slotExists = existingSlots.some(
                                (slot) => slot.from === from && slot.to === to
                            );
                            
                            if (!slotExists) {
                                // Create new time slot with service information
                                const newSlot: TimeRange = {
                                    from,
                                    to,
                                    _id: Math.floor(Math.random() * 99999).toString(),
                                    services: [
                                        {
                                            id: service._id,
                                            service_available_days: [daySlot],
                                        },
                                    ],
                                };
                                
                                mergedSchedule[dayName] = [...existingSlots, newSlot];
                            } else {
                                // Add service to existing slot if not already there
                                const slotIndex = existingSlots.findIndex(
                                    (slot) => slot.from === from && slot.to === to
                                );
                                if (slotIndex !== -1) {
                                    const existingSlot = existingSlots[slotIndex];
                                    if (existingSlot.services && Array.isArray(existingSlot.services)) {
                                        const serviceExists = existingSlot.services.some(
                                            (s) => typeof s === 'object' && 'id' in s && s.id === service._id
                                        );
                                        if (!serviceExists) {
                                            existingSlot.services.push({
                                                id: service._id,
                                                service_available_days: [daySlot],
                                            });
                                        }
                                    } else {
                                        existingSlot.services = [
                                            {
                                                id: service._id,
                                                service_available_days: [daySlot],
                                            },
                                        ];
                                    }
                                }
                            }
                        }
                    });
                }
            });
            
            // Sort each day's slots by start time
            (Object.keys(mergedSchedule) as Array<keyof WeeklySchedule>).forEach((day) => {
                if (Array.isArray(mergedSchedule[day])) {
                    mergedSchedule[day] = [...mergedSchedule[day]].sort(
                        (a, b) => a.from - b.from
                    );
                }
            });
            
            dispatch({ type: 'SET_SCHEDULE', payload: mergedSchedule });
        } else if (scheduleData.data) {
            // If only schedule data is available, use it as is
            dispatch({ type: 'SET_SCHEDULE', payload: scheduleData.data });
        }
    }, [scheduleData.data, privateServicesData.data]);
    useEffect(() => {
        // restore the schedule to the original state just 2 lines in the bottom
        const updateSchedule = async () => {
            const sortedSchedule = Object.fromEntries(
                Object.entries(state.schedule).map(([day, slots]) => [
                    day,
                    Array.isArray(slots) 
                        ? [...slots].sort((a, b) => Number(a.from) - Number(b.from))
                        : []
                ])
            ) as WeeklySchedule;
            dispatch({ type: 'SET_SCHEDULE', payload: sortedSchedule });

            // Bug: this overwrote the expert's general schedule when only per-service hours
            // were meant to be updated. Kept as a comment for reference.
            // axios.put(`${constants.API_URL}/instructor/admin/schedule`, { schedule: sortedSchedule});
            axios.put(`${constants.API_URL}/instructor/admin/private_services_schedule`, { schedule: sortedSchedule});

            
            alert?.showAlert(t('workingHoursUpdated'));
        };
        if (state.updated) {
            updateSchedule();
            dispatch({ type: 'CLEAR_UPDATED' });
        }
    }, [state, alert]);
    const [vacationError, setVacationError] = useState<string>('');
    const [showVacationDialog, setShowVacationDialog] = useState(false);
    const onClearVacation = async () => {
        setVacation(undefined);
        setShowVacationDialog(false);
        await axios.delete(`${constants.API_URL}/instructor/admin/vacation`);
        alert?.showAlert(t('vacationDeleted'));
    };
    const [vacation, setVacation] = useState<{ start?: Date; end?: Date } | undefined>(profileData?.vacation);
    const onUpdateVacation = async ({ start, end }: { start: Date; end: Date }) => {
        setVacation({ start, end });
        if (!start || !end) throw new Error('חובה להגדיר תאריך התחלה וסיום');
        if (start > end) {
            throw new Error('סיום החופשה חייב להיות לאחר תחילת החופשה');
        } else {
            await axios.post(`${constants.API_URL}/instructor/admin/vacation`, { start, end });
            alert?.showAlert(t('vacationSet'));
        }
    };

    const modal = useModal();

    const formatTime = (time: number) => `${String(Math.floor(time / 100)).padStart(2, '0')}:${String(time % 100).padStart(2, '0')}`;
    const services = useMemo(() => {
        if (privateServicesData.data && Array.isArray(privateServicesData.data)) {
            return privateServicesData.data.filter((service) => service.active !== false && service.title?.he);
        }
        return [];
    }, [privateServicesData.data]);
    const ALL_SERVICES_VALUE = 'all';
    const getServiceLabel = (servicesSelection?: ServiceSelection[] | 'all') => {
        if (!servicesSelection) return '';
        if (servicesSelection === ALL_SERVICES_VALUE) return t('allServices');
        if (Array.isArray(servicesSelection) && servicesSelection.length === 0) return '';
        if (Array.isArray(servicesSelection)) {
            // Map service IDs back to service titles for display
            return servicesSelection
                .map((serviceSel) => {
                    const service = services.find((s) => s._id === serviceSel.id);
                    return service?.title?.he || '';
                })
                .filter(Boolean)
                .join(', ');
        }
        return '';
    };

    return (
        <>
            <VacationDialog
                onClearVacation={onClearVacation}
                onUpdateVacation={onUpdateVacation}
                vacationError={vacationError}
                setVacationError={setVacationError}
                isOpen={showVacationDialog}
                onClose={() => setShowVacationDialog(false)}
                initVacation={profileData?.vacation}
                vacation={vacation}
                setVacation={setVacation}
            />
            <div className='p-2'>
                <div className='flex flex-col gap-2 mb-8'>
                    {days.map((day, dayIndex) => {
                        return (
                            <div>
                                <Typography variant='h6'>{dayNames[dayIndex]}</Typography>
                                <div className='flex gap-2 flex-row flex-wrap items-center'>
                                    {state.schedule[day.toLowerCase() as keyof WeeklySchedule]?.map((time) => {
                                        return (
                                            <span className='flex gap-4 py-2 pe-2 ps-4 border rounded-full justify-between items-center flex-wrap'>
                                                <span className='flex flex-1 items-center gap-2'>
                                                    <span>{formatTime(time.from)}</span>
                                                    <span className='text-[10px]'>
                                                        <ArrowBack fontSize='small' />
                                                    </span>
                                                    <span>{formatTime(time.to)}</span>
                                                </span>
                                                {getServiceLabel(time.services) && (
                                                    <span className='text-sm text-gray-500 whitespace-normal break-words max-w-xs'>{getServiceLabel(time.services)}</span>
                                                )}
                                                <button
                                                    type='button'
                                                    className='hover:text-black text-gray-400 transition-all flex-shrink-0'
                                                    onClick={() => {
                                                        dispatch({ type: 'REMOVE_DAY_TIME', payload: { day: day.toLowerCase() as keyof WeeklySchedule, _id: time._id } });
                                                    }}>
                                                    <Close />
                                                </button>
                                            </span>
                                        );
                                    })}
                                    <button
                                        className='border rounded-full aspect-square w-8 h-8 border-blue-400 hover:bg-blue-400 hover:text-white transition-colors'
                                        type='button'
                                        onClick={() => {
                                            let localStartDate: null | number = null;
                                            let localEndDate: null | number = null;
                                            let localServices: string[] | 'all' = [];
                                            let isStartTimeValid = false;
                                            let isEndTimeValid = false;

                                            const handleServiceChange = (event: SelectChangeEvent<string[]>) => {
                                                const {
                                                    target: { value },
                                                } = event;
                                                const normalized = typeof value === 'string' ? value.split(',') : (value as string[]);
                                                if (normalized.includes(ALL_SERVICES_VALUE)) {
                                                    localServices = ALL_SERVICES_VALUE;
                                                } else {
                                                    localServices = normalized as string[];
                                                }
                                            };

                                            modal.show({
                                                title: `${t('addHoursDialogTitle')} ${dayNames[dayIndex]}`,
                                                body: (
                                                    <div dir='ltr' className='flex items-center gap-4 justify-center flex-col  '>
                                                        <div className='text-right'>
                                                            <h5>{t('start')}</h5>
                                                            <TimeInput
                                                                key={'start'}
                                                                onChange={(v) => {
                                                                    console.log('Start time', v);
                                                                    localStartDate = v; // Update the local variable
                                                                }}
                                                                onValidationChange={(isValid) => {
                                                                    isStartTimeValid = isValid;
                                                                }}
                                                            />
                                                        </div>
                                                        <div className='text-right'>
                                                            <h5>{t('end')}</h5>
                                                            <TimeInput
                                                                key={'end'}
                                                                onChange={(v) => {
                                                                    console.log('End time', v);
                                                                    localEndDate = v; // Update the local variable
                                                                }}
                                                                onValidationChange={(isValid) => {
                                                                    isEndTimeValid = isValid;
                                                                }}
                                                            />
                                                        </div>
                                                        <div className='w-full'>
                                                            <FormControl fullWidth>
                                                                <InputLabel id='service-select-label'>{t('service')}</InputLabel>
                                                                <Select<string[]>
                                                                    labelId='service-select-label'
                                                                    label={t('service')}
                                                                    multiple
                                                                    defaultValue={[]}
                                                                    onChange={handleServiceChange}
                                                                    renderValue={(selected) => {
                                                                        if (!Array.isArray(selected) || selected.length === 0) {
                                                                            return t('selectServices');
                                                                        }

                                                                        // If "all" is selected, always show the label for all services
                                                                        if (selected.includes(ALL_SERVICES_VALUE)) {
                                                                            return t('allServices');
                                                                        }

                                                                        // Map selected IDs to service titles
                                                                        const titles = selected
                                                                            .map((id) => {
                                                                                const service = services.find((s) => s._id === id);
                                                                                return service?.title?.he || '';
                                                                            })
                                                                            .filter(Boolean);

                                                                        return titles.length > 0 ? titles.join(', ') : t('selectServices');
                                                                    }}
                                                                >
                                                                    {/* <MenuItem value={ALL_SERVICES_VALUE}>
                                                                        <em>כל השירותים</em>
                                                                    </MenuItem> */}
                                                                    {services.map((service) => (
                                                                        <MenuItem key={service._id} value={service._id}>
                                                                            {service.title?.he}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </div>
                                                    </div>
                                                ),
                                                footer: (
                                                    <Button
                                                        onClick={() => {
                                                            // אם שעות או דקות לא מוזנים/לא תקינים, לא לעשות כלום
                                                            if (!isStartTimeValid || !isEndTimeValid || localStartDate === null || localEndDate === null) {
                                                                return;
                                                            }
                                                            
                                                            let resolvedServices: ServiceSelection[] | 'all' | null = null;
                                                            
                                                            if (localServices === ALL_SERVICES_VALUE) {
                                                                resolvedServices = ALL_SERVICES_VALUE;
                                                            } else if (Array.isArray(localServices) && localServices.length > 0) {
                                                                // Convert time from number format (e.g., 900) to "HH:mm" format
                                                                const startTimeStr = `${String(Math.floor(localStartDate / 100)).padStart(2, '0')}:${String(localStartDate % 100).padStart(2, '0')}`;
                                                                const endTimeStr = `${String(Math.floor(localEndDate / 100)).padStart(2, '0')}:${String(localEndDate % 100).padStart(2, '0')}`;
                                                                
                                                                // Map service IDs to ServiceSelection objects with schedule from the selected day/time
                                                                resolvedServices = localServices
                                                                    .map((serviceId) => {
                                                                        const service = services.find((s) => s._id === serviceId);
                                                                        if (service) {
                                                                            return {
                                                                                id: service._id,
                                                                                service_available_days: [
                                                                                    {
                                                                                        day: dayIndex,
                                                                                        start_time: startTimeStr,
                                                                                        end_time: endTimeStr,
                                                                                    }
                                                                                ],
                                                                            };
                                                                        }
                                                                        return null;
                                                                    })
                                                                    .filter((s): s is ServiceSelection => s !== null);
                                                            }
                                                            
                                                            if (localStartDate < localEndDate && resolvedServices) {
                                                                dispatch({
                                                                    type: 'ADD_DAY_TIME',
                                                                    payload: {
                                                                        day: days[dayIndex] as keyof WeeklySchedule,
                                                                        time: {
                                                                            from: localStartDate,
                                                                            to: localEndDate,
                                                                            _id: Math.floor(Math.random() * 99999).toString(),
                                                                            services: resolvedServices,
                                                                        },
                                                                    },
                                                                });


                                                                modal.hide();
                                                            }
                                                        }}>
                                                        ADD
                                                    </Button>
                                                ),
                                            });
                                        }}>
                                        +
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <Stack direction='row' gap={2} alignItems={'center'} my={3}>
                    <button className='bg-blue-200 px-4 py-1 rounded-full hover:scale-105 transition-all' type='button' onClick={() => setShowVacationDialog(true)}>
                        {t('setVacation')}
                    </button>

                    {vacation && (
                        <Typography variant='body2' textAlign='center' whiteSpace={'pre-wrap'}>{`חופשה מוגדרת:\tהתחלה: ${moment(vacation.start).format(
                            'DD/MM/YY HH:mm'
                        )}\t\t\tסיום: ${moment(vacation.end).format('DD/MM/YY HH:mm')}`}</Typography>
                    )}
                </Stack>
            </div>
        </>
    );
};

export default WorkingHoursServices;
